/**
 * Budget Helper Functions
 *
 * 예산 관리를 위한 유틸리티 함수들
 * - Cascade delete: 대분류 삭제 시 모든 소분류도 함께 삭제
 * - Orphan detection: 부모가 없거나 비활성화된 예산 탐지
 * - Data cleanup: 고아 예산 정리
 */

import type { BudgetRepository } from '../repositories/BudgetRepository'
import type { Budget } from '../types/Budget'

/**
 * Cascade delete: 대분류 + 모든 소분류 삭제
 *
 * 대분류 예산을 삭제할 때, 해당 대분류에 속한 모든 소분류 예산도 함께 삭제합니다.
 * Soft delete 패턴을 사용하므로 실제로는 isActive: false로 마킹됩니다.
 *
 * 로직:
 * 1. 대분류에 속한 모든 소분류 예산 조회 (parentBudgetId === majorBudgetId)
 * 2. 모든 소분류 먼저 삭제
 * 3. 대분류 삭제
 * 4. 삭제된 모든 예산 ID 반환
 *
 * @param budgetRepository Budget repository instance
 * @param majorBudgetId 삭제할 대분류 예산 ID
 * @returns 삭제된 예산 ID 배열 (소분류들 + 대분류)
 *
 * @example
 * ```typescript
 * // 대분류 "식비"와 소분류 "외식비", "식재료비" 모두 삭제
 * const deletedIds = await cascadeDeleteBudget(budgetRepository, 'budget_001')
 * // => ['budget_002', 'budget_003', 'budget_001']
 * ```
 */
export async function cascadeDeleteBudget(
  budgetRepository: BudgetRepository,
  majorBudgetId: string
): Promise<string[]> {
  const deletedIds: string[] = []

  // 1. 해당 대분류에 속한 모든 소분류 예산 조회
  const minorBudgets = await budgetRepository.findByParentBudgetId(majorBudgetId)

  // 2. 모든 소분류 먼저 삭제 (cascade)
  for (const minorBudget of minorBudgets) {
    await budgetRepository.delete(minorBudget.id)
    deletedIds.push(minorBudget.id)
    console.log(`🗑️  소분류 cascade 삭제: ${minorBudget.categoryId} (${minorBudget.id})`)
  }

  // 3. 대분류 삭제
  await budgetRepository.delete(majorBudgetId)
  deletedIds.push(majorBudgetId)
  console.log(`🗑️  대분류 삭제: ${majorBudgetId}`)

  return deletedIds
}

/**
 * Orphan budget 탐지
 *
 * 부모가 없거나 비활성화된 소분류 예산들을 찾습니다.
 * 이런 orphan 예산들은 데이터 무결성 문제를 나타냅니다.
 *
 * Orphan 종류:
 * 1. parent_not_found: parentBudgetId가 가리키는 예산이 존재하지 않음
 * 2. parent_inactive: parentBudgetId가 가리키는 예산이 isActive: false
 *
 * @param budgetRepository Budget repository instance
 * @returns Orphan 예산 배열 (각 항목은 { budget, reason } 형태)
 *
 * @example
 * ```typescript
 * const orphans = await findOrphanedBudgets(budgetRepository)
 * orphans.forEach(({ budget, reason }) => {
 *   console.log(`Orphan: ${budget.categoryId} - ${reason}`)
 * })
 * ```
 */
export async function findOrphanedBudgets(
  budgetRepository: BudgetRepository
): Promise<Array<{ budget: Budget; reason: 'parent_not_found' | 'parent_inactive' }>> {
  // 1. 모든 활성 예산 조회
  const allBudgets = await budgetRepository.findAll({ isActive: true })
  const orphans: Array<{ budget: Budget; reason: 'parent_not_found' | 'parent_inactive' }> = []

  // 2. 각 예산에 대해 부모 상태 확인
  for (const budget of allBudgets) {
    // 대분류 예산은 스킵 (parentBudgetId가 없음)
    if (!budget.parentBudgetId) continue

    // 부모 예산 조회
    const parent = await budgetRepository.findById(budget.parentBudgetId)

    if (!parent) {
      // 부모가 존재하지 않음
      orphans.push({ budget, reason: 'parent_not_found' })
    } else if (!parent.isActive) {
      // 부모가 비활성화됨
      orphans.push({ budget, reason: 'parent_inactive' })
    }
  }

  return orphans
}

/**
 * Orphan budget 정리
 *
 * 모든 고아 예산들을 찾아서 삭제(비활성화)합니다.
 * Soft delete 패턴이므로 필요시 복구 가능합니다.
 *
 * @param budgetRepository Budget repository instance
 * @returns 정리된 예산 개수
 *
 * @example
 * ```typescript
 * const cleanedCount = await cleanupOrphanedBudgets(budgetRepository)
 * console.log(`${cleanedCount}개의 고아 예산을 정리했습니다`)
 * ```
 */
export async function cleanupOrphanedBudgets(
  budgetRepository: BudgetRepository
): Promise<number> {
  // 1. 모든 orphan 예산 찾기
  const orphans = await findOrphanedBudgets(budgetRepository)

  // 2. 각 orphan 삭제
  for (const { budget, reason } of orphans) {
    await budgetRepository.delete(budget.id)
    console.log(`🧹 정리: ${budget.categoryId} (${budget.id}) - reason: ${reason}`)
  }

  return orphans.length
}
